import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';

interface AddNoteFormProps {
  onSubmit: (text: string) => Promise<void>;
  onCancel: () => void;
  isTask: boolean;
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({ onSubmit, onCancel, isTask }) => {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!text.trim()) {
      setError('Note text cannot be empty');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // is_done always false when creating a new task
      await onSubmit(text.trim());
      setText('');
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create note');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Note"
        placeholder="Enter note about this user..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={isSubmitting}
        sx={{ mb: 2 }}
      />



      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={isSubmitting}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || !text.trim()}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {isSubmitting ? 'Adding...' : 'Add Note'}
        </Button>
      </Box>
    </Box>
  );
};

export default AddNoteForm;
